import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFamilyContext } from '../context/FamilyContext';

const { FiX, FiUser, FiPhone, FiActivity, FiMapPin, FiUsers, FiCheck } = FiIcons;

export default function ContactModal({ contact, onClose, onSave }) {
  const { state } = useFamilyContext();
  const { familyMembers } = state;
  const [formData, setFormData] = useState({
    name: '',
    role: 'Arzt',
    phone: '',
    address: '',
    notes: '',
    people: []
  });

  useEffect(() => {
    if (contact) setFormData(contact);
  }, [contact]);

  const togglePerson = (personId) => {
    setFormData(prev => {
      const people = prev.people || [];
      if (people.includes(personId)) {
        return { ...prev, people: people.filter(id => id !== personId) };
      } else {
        return { ...prev, people: [...people, personId] };
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        />
        
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden z-10"
        >
          <div className="p-6 overflow-y-auto max-h-[90vh]">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {contact ? 'Kontakt bearbeiten' : 'Neuer Notfallkontakt'}
              </h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Name *</label>
                <div className="relative">
                   <SafeIcon icon={FiUser} className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    className="w-full pl-10 border border-gray-200 rounded-lg px-3 py-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Rolle / Typ</label>
                <select
                  value={formData.role}
                  onChange={e => setFormData({ ...formData, role: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                >
                  <option value="Arzt">Arzt</option>
                  <option value="Zahnarzt">Zahnarzt</option>
                  <option value="Therapeut">Therapeut</option>
                  <option value="Familie">Familie</option>
                  <option value="Nachbar">Nachbar</option>
                  <option value="Dienstleistung">Dienstleistung</option>
                  <option value="Sonstiges">Sonstiges</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Telefon *</label>
                <div className="relative">
                   <SafeIcon icon={FiPhone} className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full pl-10 border border-gray-200 rounded-lg px-3 py-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Adresse</label>
                <div className="relative">
                   <SafeIcon icon={FiMapPin} className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={formData.address}
                    onChange={e => setFormData({ ...formData, address: e.target.value })}
                    className="w-full pl-10 border border-gray-200 rounded-lg px-3 py-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 dark:text-gray-300 flex items-center">
                   <SafeIcon icon={FiUsers} className="w-4 h-4 mr-1" /> Zugeordnet zu
                </label>
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-1">
                  {familyMembers.map(member => (
                    <div 
                      key={member.id}
                      onClick={() => togglePerson(member.id)}
                      className={`cursor-pointer border rounded-lg p-2 flex items-center space-x-2 transition-all ${
                        (formData.people || []).includes(member.id) 
                          ? 'bg-blue-50 border-blue-500 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200' 
                          : 'hover:bg-gray-50 border-gray-200 dark:hover:bg-gray-700 dark:border-gray-600 dark:text-gray-300'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                        (formData.people || []).includes(member.id) ? 'bg-blue-500 border-blue-500' : 'border-gray-400'
                      }`}>
                         {formData.people?.includes(member.id) && <SafeIcon icon={FiCheck} className="w-3 h-3 text-white" />}
                      </div>
                      <span className="text-xs truncate font-medium">{member.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-6">
                <button 
                  type="button" 
                  onClick={onClose} 
                  className="px-6 py-2.5 text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 transition-colors font-semibold"
                >
                  Abbrechen
                </button>
                <button 
                  type="submit" 
                  className="px-8 py-2.5 text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/30 font-semibold"
                >
                  Speichern
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}